import { CKEditor } from 'ckeditor4-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { registerImageUploadDialog } from './registerImageUploadDialog';
import './ContentEditor.style.scss';

const CKEDITOR_BASE_URL = `${process.env.PUBLIC_URL || ''}/ckeditor`;

if (typeof window !== 'undefined' && !window.CKEDITOR_BASEPATH) {
	window.CKEDITOR_BASEPATH = `${CKEDITOR_BASE_URL}/`;
}

function ContentEditor({ initContent, onChange, title, tip=null, required=false, fieldKey }) {
	const { t } = useTranslation();
	const [isEditorReady, setIsEditorReady] = useState(false);

	useEffect(() => {
		const timer = window.setTimeout(() => {
			setIsEditorReady(true);
		}, 0);

		return () => {
			window.clearTimeout(timer);
			setIsEditorReady(false);
		};
	}, []);

	const handleNamespaceLoaded = useCallback(CKEDITOR => {
		registerImageUploadDialog(CKEDITOR, () => ({
			upload: t('upload'),
			uploading: t('uploading'),
			imageOnlyMessage: t('image_type_require_message'),
			maxSizeMessage: `${t('max_file_size')}: ${2} ${t('mb')}`,
			apiMessageKey: t('api_message')
		}));
	}, [t]);

	const editorConfig = useMemo(() => ({
		language: 'fa',
		contentsLangDirection: 'rtl',
		contentsCss: [
			`${CKEDITOR_BASE_URL}/contents.css`,
			`${process.env.PUBLIC_URL || ''}/statics/styles/ckeditor-contents.css`
		],
		toolbar: [
			{ name: 'document', items: [ 'Source', '-', 'Save', 'NewPage', 'Preview', 'Print', '-', 'Templates' ] },
			{ name: 'clipboard', items: [ 'Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord', '-', 'Undo', 'Redo' ] },
			{ name: 'editing', items: [ 'Find', 'Replace', '-', 'SelectAll', '-', 'Scayt' ] },
			{ name: 'forms', items: [ 'Form', 'Checkbox', 'Radio', 'TextField', 'Textarea', 'Select', 'Button', 'ImageButton', 'HiddenField' ] },
			'/',
			{ name: 'basicstyles', items: [ 'Bold', 'Italic', 'Underline', 'Strike', 'Subscript', 'Superscript', '-', 'CopyFormatting', 'RemoveFormat' ] },
			{ name: 'paragraph', items: [ 'NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote', 'CreateDiv', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock', '-', 'BidiLtr', 'BidiRtl', 'Language' ] },
			{ name: 'links', items: [ 'Link', 'Unlink', 'Anchor' ] },
			{ name: 'insert', items: [ 'Image', 'Flash', 'Table', 'HorizontalRule', 'Smiley', 'SpecialChar', 'PageBreak', 'Iframe' ] },
			'/',
			{ name: 'styles', items: [ 'Styles', 'Format', 'Font', 'FontSize' ] },
			{ name: 'colors', items: [ 'TextColor', 'BGColor' ] },
			{ name: 'tools', items: [ 'Maximize', 'ShowBlocks' ] },
			{ name: 'about', items: [ 'About' ] }
		],
		removePlugins: 'elementspath',
		delayIfDetached: true,
		delayIfDetached_interval: 50,
		resize_enabled: true,
		versionCheck: false,
	}), []);

	return (
		<div className={"col-12 mb-3"}>
			<label className="form-label">
				{title} {tip ? <small>({tip})</small> : ''}
				{required ? <span className="text-danger"> *</span> : ''}
			</label>
			{isEditorReady &&
				<CKEditor
					id={fieldKey}
					name={fieldKey}
					editorUrl={`${CKEDITOR_BASE_URL}/ckeditor.js`}
					initData = {initContent}
					onChange = {onChange}
					onNamespaceLoaded={handleNamespaceLoaded}
					config = {editorConfig}
				/>
			}
		</div>
	);
}

export default ContentEditor;
